import classNames from "classnames"
import React, { useCallback } from "react"

import { Analytics } from "@bounce/analytics"
import { Button, Title6 } from "@bounce/design-system"
import { useTranslation } from "@bounce/i18n"

import SafeLink from "@/components/Generic/SafeLink/SafeLink"
import { InteractionEvents } from "@/libs/analytics/events"

import CrossIcon from "@bounce/assets/icons/Cross.svg"

type StorePreviewCardProps = {
  className?: string
  href: string
  nickname: string
  onDismiss: () => void
}

const StorePreviewCard = ({ nickname, className, href, onDismiss }: StorePreviewCardProps) => {
  const { t } = useTranslation()

  const onClick = useCallback(
    (ev: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      ev.preventDefault()
      Analytics.track(InteractionEvents.MapTooltipCtaPressed, { destination: href })
      location.assign(href)
    },
    [href],
  )

  return (
    <div
      className={classNames(
        "relative w-[220px] rounded-2xl bg-white p-4 shadow md:w-[300px]",
        "border border-gray-100",
        className,
      )}
      data-testid="store-preview-card-marketing">
      <div className="flex items-center justify-between">
        <Title6 className="mr-2 truncate">{nickname}</Title6>
        <button
          onClick={e => {
            e.stopPropagation()
            onDismiss()
          }}
          type="button"
          className="mb-1 flex size-8 flex-shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 hover:bg-gray-200"
          aria-label={t("cmp.interactiveCityMap.storePreviewCard.closeLabel", "Close")}>
          <CrossIcon />
        </button>
      </div>

      <div className="flex items-start gap-3">
        <div className="flex flex-1 flex-col gap-1 truncate">
          <div className="flex items-center text-sm">
            <SafeLink href={href} onClick={onClick} target="_blank">
              <Button size="small" onPress={null}>
                {t("cmp.interactiveCityMap.storePreviewCard.viewLocation", "View location")}
              </Button>
            </SafeLink>
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-1/2 -mb-2 size-4 -translate-x-1/2 rotate-45 transform overflow-hidden border-b border-r border-gray-100 bg-white" />
    </div>
  )
}

export default StorePreviewCard
